import { Container } from 'react-bootstrap'
import { getIntegrationDisplayName, GOTO_HELP_ARTICLE_URL, IntegrationServiceId, TEAMS_CALLS_HELP_ARTICLE_URL } from '../../../common/integrations'
import './IntegrationAbout.scss'
import React from 'react'
import { Link } from 'react-router-dom'
import { HowItWorksComponent } from './HowItWorks/HowItWorksComponent'
import { IntegrationUpsellBanner } from './IntegrationUpsellBanner'

const PhoneAbout: React.FC = () => {
  return (
    <>
      <p>
        The Phone integration allows you to claim and manage phone numbers directly from your Professional Dashboard account. With this integration, you can set
        up business phone lines, manage call forwarding, and access voicemail features to enhance your communication with clients.
      </p>
      <p>Key features include:</p>
      <ul>
        <li>Claim new phone numbers for your business</li>
        <li>Manage multiple phone lines</li>
        <li>Set up call forwarding and voicemail</li>
        <li>Access call logs and analytics</li>
      </ul>
      <p>To get started, connect the Phone integration and follow the prompts to claim your desired phone number.</p>
    </>
  )
}

export interface IntegrationAboutTabProps {
  serviceId: IntegrationServiceId
}

const AboutComponent: React.FC<{ serviceId: IntegrationServiceId }> = ({ serviceId }) => {
  switch (serviceId) {
    case IntegrationServiceId.PHONE:
      return <PhoneAbout />
    case IntegrationServiceId.ZOOM:
      return (
        <div>
          Connect your Zoom and Zocks accounts to capture notes and tasks from both your Zoom Meetings and Zoom Phone calls. With Zocks' native app for Zoom,
          you can also get access to everything you need, manage tasks and sync client data right within Zoom.
        </div>
      )
    case IntegrationServiceId.MEET:
    case IntegrationServiceId.TEAMS:
      return (
        <div>
          Easily sync with your calendar or{' '}
          <Link to='/workspace/upcoming' className='common-card-description-link' onClick={(e) => e.stopPropagation()} target='_blank'>
            invite
          </Link>{' '}
          your Zocks AI Notetaker to capture notes and tasks, so you never miss a detail.
        </div>
      )
    case IntegrationServiceId.WEBEX:
      return <div>Connect Zocks with Webex to capture notes and tasks, so you never miss a detail.</div>
    case IntegrationServiceId.GOOGLE_CALENDAR:
      return (
        <div>Sync your meetings effortlessly with your Google Calendar to stay on top of your schedule and never miss an important client conversation.</div>
      )
    case IntegrationServiceId.OUTLOOK_CALENDAR:
      return (
        <div>Sync your meetings effortlessly with your Outlook Calendar to stay on top of your schedule and never miss an important client conversation.</div>
      )
    case IntegrationServiceId.EXCHANGE_CALENDAR:
      return (
        <div>Sync your meetings effortlessly with your Exchange Calendar to stay on top of your schedule and never miss an important client conversation.</div>
      )
    case IntegrationServiceId.GMAIL:
      return (
        <div>
          Connect your accounts to send emails through your Gmail account. Turn your email into a data source for client insights, automated task creation, and
          profile updates.
        </div>
      )
    case IntegrationServiceId.OUTLOOK_MAIL:
      return (
        <div>
          Send emails through your Outlook account. Turn your email into a data source for client insights, automated task creation, and profile updates.
          Increase your productivity with Zocks' Native Outlook App.
        </div>
      )
    case IntegrationServiceId.EXCHANGE_MAIL:
      return (
        <div>
          Send emails through your on-premise Exchange account. Turn your email into a data source for client insights, automated task creation, and profile
          updates.
        </div>
      )
    case IntegrationServiceId.LINKEDIN:
      return (
        <div>
          Connect LinkedIn with Zocks to streamline workflows and bring professional networking into your broader client engagement strategy. Enhance
          connectivity, improve efficiency, and create new opportunities through a more integrated experience. Personally identifiable information (PII) is
          removed from AI-generated content to help protect sensitive client data. Zocks follows an advisor-in-the-loop approach, offering multiple drafts for
          review, refinement, and approval.
        </div>
      )
    case IntegrationServiceId.E_MONEY:
      return (
        <div>
          Keep your planning data effortlessly up-to-date. Automatically sync contacts, prep for meetings, and easily update 200+ eMoney Facts with insights
          from conversations, emails, documents, and other connected systems, all without manual data entry.
        </div>
      )
    case IntegrationServiceId.RIGHT_CAPITAL:
      return (
        <div>
          After every client conversation, Zocks automatically captures and easily syncs client information to RightCapital, so you can create or update client
          profiles faster and close more prospects.
        </div>
      )
    case IntegrationServiceId.CONQUEST_PLANNING:
      return (
        <div>
          Keep your planning data effortlessly up-to-date. Prep for meetings and easily update financial plans with insights from conversations, emails, and
          documents — all without manual data entry.
        </div>
      )
    case IntegrationServiceId.ORION:
    case IntegrationServiceId.BLACK_DIAMOND_WP:
      return <div>Get real-time portfolio data and insights for meeting prep, live meetings, emails and forms.</div>
    case IntegrationServiceId.HOLISTIPLAN:
      return <div>Unlock new planning opportunities with your clients by connecting Holistiplan’s advanced tax analysis with Zocks.</div>
    case IntegrationServiceId.WEALTHBOX:
      return (
        <div>
          Keep your Wealthbox CRM data effortlessly up-to-date. Automatically sync, create and update contact records, save notes, create tasks, trigger
          workflows, and prep for meetings with insights captured by Zocks from conversations, emails, documents, and other connected systems - all without
          manual data entry.
        </div>
      )
    case IntegrationServiceId.SALESFORCE:
    case IntegrationServiceId.PRACTIFI:
    case IntegrationServiceId.XLR8:
    case IntegrationServiceId.ELEMENTS:
    case IntegrationServiceId.BLACK_DIAMOND:
    case IntegrationServiceId.REDTAIL:
    case IntegrationServiceId.ZOHO:
    case IntegrationServiceId.HUBSPOT:
    case IntegrationServiceId.DYNAMICS:
    case IntegrationServiceId.ENGAGE:
    case IntegrationServiceId.SMARTOFFICE:
      return (
        <div>
          Keep your {getIntegrationDisplayName(serviceId)} CRM data effortlessly up-to-date. Automatically sync, create and update contact, save notes, create
          tasks, and prep for meetings with insights captured by Zocks from conversations, emails, documents, and other connected systems - all without manual
          data entry.
        </div>
      )
    case IntegrationServiceId.PRECISE_FP:
      return (
        <div>
          Keep your PreciseFP data up to date and synced across all your connected systems with insights captured by Zocks from conversations, emails, documents
          - all without manual data entry.
        </div>
      )
    case IntegrationServiceId.WEALTH:
      return (
        <div>
          Connect Wealth.com and Zocks to see estate planning details in your workflow — so you walk into meetings prepared and keep plans moving to completion.
        </div>
      )
    case IntegrationServiceId.WOVE:
      return <div>Get real-time portfolio data and insights for meeting prep, live meetings, emails and forms.</div>
    case IntegrationServiceId.DELEGATED_EMAIL:
      return (
        <>
          <p>
            Some advisors use more than one email address for client meetings: a work account, a personal calendar, or a second firm address. Linked emails
            makes sure Zocks doesn't miss those meetings.
          </p>
          <p>
            Link up to 2 extra email addresses to your account. Zocks will recognize calendar invitations sent to those addresses, just like it does for your
            primary email.
          </p>
        </>
      )
    case IntegrationServiceId.RINGCENTRAL:
      return (
        <div>
          Connect your RingCentral phone system to Zocks. After starting a call, merge Zocks AI Assistant into the conversation or process recordings to get
          automatic meeting notes and CRM updates.
        </div>
      )
    case IntegrationServiceId.GOTO:
      return (
        <>
          <p>
            Connect your GoTo Connect phone system to Zocks. Zocks processes GoTo Connect call recordings to generate meeting notes, summaries, transcripts,
            tasks, and CRM updates.
          </p>
          <p>
            <span className='fw-bold'>Action required in GoTo Connect:</span> before connecting Zocks, make sure call recording and recording API access are
            enabled in GoTo Admin. Zocks can only process calls when a GoTo Connect recording is available.
          </p>
          <p>If a call is not recorded in GoTo Connect, Zocks will not receive or process that call.</p>
          <p>
            Need help? See the{' '}
            <a href={GOTO_HELP_ARTICLE_URL} target='_blank' rel='noreferrer'>
              GoTo Connect setup guide
            </a>
            .
          </p>
        </>
      )
    case IntegrationServiceId.TEAMS_CALLS:
      return (
        <>
          <p>
            Connect Microsoft Teams calls to Zocks. Zocks processes the recordings of your Teams calls to generate meeting notes, summaries, transcripts, tasks,
            and CRM updates.
          </p>
          <p>
            This includes phone calls that you make or receive in Teams, 1:1 Teams calls, and group Teams calls that you start, when you record the call.
            Scheduled Teams meetings and Meet now meetings are not included. For meetings, the Zocks AI Notetaker joins when you sync your calendar or invite it
            to the meeting.
          </p>
          <p>Call capture starts when you connect. To stop call capture, disconnect the integration.</p>
          <p>
            <span className='fw-bold'>Action required in Microsoft Teams:</span> record the calls that Zocks should process. Your Microsoft 365 administrator
            must allow call recording and approve Zocks access to call recordings.
          </p>
          <p>If a call is not recorded in Microsoft Teams, Zocks will not receive or process that call.</p>
          <p>
            Need help? See the{' '}
            <a href={TEAMS_CALLS_HELP_ARTICLE_URL} target='_blank' rel='noreferrer'>
              Microsoft Teams Calls setup guide
            </a>
            .
          </p>
        </>
      )
    case IntegrationServiceId.SCHWAB:
      return (
        <div>
          Get real-time custodial data from Schwab Advisor Services — balances, holdings, transactions, and more — to power meeting prep, live meetings, emails,
          and forms.
        </div>
      )
    case IntegrationServiceId.BOX:
      return (
        <div>
          Your client documents and Zocks workflows, in one place. Connect Box once, and everything you generate in Zocks — meeting summaries, prep docs, forms
          — saves straight to the right client folder. All within your existing Box folder structure.
        </div>
      )
    case IntegrationServiceId.SHAREPOINT:
      return (
        <div>
          Your client documents and Zocks workflows, in one place. Connect SharePoint once, and everything you generate in Zocks — meeting summaries, prep docs,
          forms — saves straight to the right client folder. All within your existing SharePoint folder structure.
        </div>
      )
    case IntegrationServiceId.EGNYTE:
      return (
        <div>
          Your client documents and Zocks workflows, in one place. Connect Egnyte once, and everything you generate in Zocks — meeting summaries, prep docs,
          forms — saves straight to the right client folder. All within your existing Egnyte folder structure.
        </div>
      )
    case IntegrationServiceId.ONEDRIVE:
      return (
        <div>
          Your client documents and Zocks workflows, in one place. Connect OneDrive once, and everything you generate in Zocks — meeting summaries, prep docs,
          forms — saves straight to the right client folder. All within your existing OneDrive folder structure.
        </div>
      )
    case IntegrationServiceId.GOOGLE_DRIVE:
      return (
        <div>
          Your client documents and Zocks workflows, in one place. Connect Google Drive once, and everything you generate in Zocks — meeting summaries, prep
          docs, forms — saves straight to the right client folder. All within your existing Google Drive folder structure.
        </div>
      )
  }
  return <div>No additional information available for this integration.</div>
}

export const IntegrationAboutTab: React.FC<IntegrationAboutTabProps> = ({ serviceId }) => {
  return (
    <Container fluid>
      <h4 className='mb-3'>Overview</h4>
      <div className='about-container p-3 w-75 mw-800'>
        <AboutComponent serviceId={serviceId} />
      </div>
      <IntegrationUpsellBanner serviceId={serviceId} />
      <div className='how-it-works-container mt-5 mb-3 w-75 mw-800 d-flex flex-column p-3 justify-content-center align-items-start'>
        <h4 className='how-it-works-title mb-3'>How it works</h4>
        <div>
          <HowItWorksComponent serviceId={serviceId} />
        </div>
      </div>
    </Container>
  )
}
